<script setup lang="ts">
import { Head, Link, useForm } from "@inertiajs/vue3"
import { router } from "@inertiajs/vue3"
import { useDebounceFn } from "@vueuse/core"
import { DownloadIcon, SearchIcon } from "lucide-vue-next"
import { watch } from "vue"

import Heading from "@/components/Heading.vue"
import ProgressTeamHint from "@/components/ProgressTeamHint.vue"
import InputDebounced from "@/components/custom/InputDebounced.vue"
import Pagination from "@/components/custom/Pagination.vue"
import Table from "@/components/custom/Table.vue"
import Button from "@/components/ui/button/Button.vue"
import FileUploader from "@/components/ui/file-uploader/FileUploader.vue"
import { InputGroup, InputGroupAddon } from "@/components/ui/input-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout.vue"
import { dateFormatter } from "@/lib/formatters"
import { download } from "@/lib/utils"
import {
  downloadImportTemplateManagerEmployeesPath,
  exportManagerEmployeesPath,
  importManagerEmployeesPath,
  managerEmployeePath,
  managerEmployeesPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { ManagerEmployeesIndex, User } from "@/types/serializers"
import type { Column } from "@/types/table"

const props = defineProps<ManagerEmployeesIndex>()

const form = useForm<{
  file: File | null
}>({
  file: null,
})

const formFilters = useForm<{
  search?: string
  contract?: string
  department?: string
  sort_by?: string
  order?: string
}>({
  search: props.query.search || undefined,
  contract: props.query.contract || undefined,
  department: props.query.department || undefined,
  sort_by: props.query.sort_by || undefined,
  order: props.query.order || undefined,
})

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Perfiles de Empleados",
    href: managerEmployeesPath(),
  },
]

const contractsItems = [
  { value: null, label: "Todos los estados" },
  { value: "COMERCIA", label: "COMERCIA" },
  { value: "NON COMERCIA", label: "NON COMERCIA" },
]

const departmentItems = [
  { value: null, label: "Todos los colectivos" },
  { value: "PEOPLE", label: "PEOPLE" },
  { value: "OFFICE", label: "OFFICE" },
  { value: "SALES", label: "SALES" },
  { value: "UP", label: "UP" },
]

const columns: Array<Column<User>> = [
  {
    key: "ref",
    header: "Ref",
    sortable: true,
  },
  {
    key: "dni",
    header: "DNI",
    sortable: true,
  },
  {
    key: "preferred_name",
    header: "Nombre Preferido",
    sortable: true,
  },
  {
    key: "first_contract",
    header: "Colectivo (contrato previo)",
    sortable: true,
  },
  {
    key: "department",
    header: "Departamento",
    sortable: true,
  },
  {
    key: "job_title",
    header: "Job Title",
    sortable: true,
  },
  {
    key: "email",
    header: "Nuevo email Global",
    sortable: true,
  },
  {
    key: "completeness",
    header: "Completitud de perfil",
    sortable: true,
  },
  {
    key: "start_date",
    header: "Fecha inicio en Comercia",
    sortable: true,
  },
  {
    key: "termination_date",
    header: "Fecha fin en Comercia",
    sortable: true,
  },
  {
    key: "updated_at",
    header: "Updated at",
    right: true,
    sortable: true,
  },
]

async function exportUsers() {
  const XCSRFToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")
  if (!XCSRFToken) {
    console.error("CSRF token not found")
    return
  }
  try {
    const response = await fetch(exportManagerEmployeesPath(), {
      method: "POST",
      headers: { "X-CSRF-Token": XCSRFToken },
    })

    if (!response.ok) throw new Error("Export failed")

    const blob = await response.blob()
    console.log(response.headers.get("Content-Disposition"))
    const filename =
      response.headers
        .get("Content-Disposition")
        ?.match(/filename="(.+)"/)?.[1] || `employees-${Date.now()}.xlsx`
    download(blob, filename)
  } catch (error) {
    console.error("Export error:", error)
  }
}

function handleFilesChange(files: FileList | null) {
  if (files && files[0]) {
    form.file = files[0]
    form.post(importManagerEmployeesPath())
  }
}

function refreshData() {
  router.get(
    managerEmployeesPath(),
    { ...props.query, ...formFilters.data(), page: undefined },
    { preserveState: true, preserveScroll: true },
  )
}

const debouncedRefreshData = useDebounceFn(refreshData, 100)

watch(formFilters, debouncedRefreshData)
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="px-6 py-4">
      <Heading
        title="Perfiles de Empleados"
        description="Gestiona los perfiles de los empleados"
      />

      <ProgressTeamHint class="mt-2">
        <p>
          Download the import
          <a
            class="font-semibold underline"
            :href="downloadImportTemplateManagerEmployeesPath()"
            download
            >template file</a
          >, fill it with employee data and upload it to
        </p>
      </ProgressTeamHint>

      <div class="mt-4 flex items-center gap-2">
        <InputGroup class="w-88">
          <InputDebounced
            :debounce="400"
            group
            placeholder="Buscar por nombre, equipo o departamento..."
            v-model="formFilters.search"
          />
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
        </InputGroup>

        <Select v-model="formFilters.contract">
          <SelectTrigger class="w-44">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="item in contractsItems"
              :key="item.label"
              :value="item.value"
              >{{ item.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <Select v-model="formFilters.department">
          <SelectTrigger class="w-44">
            <SelectValue placeholder="Todos los colectivos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="item in departmentItems"
              :key="item.label"
              :value="item.value"
            >
              {{ item.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <div class="ml-auto flex items-center justify-between">
          <FileUploader accept=".xlsx" @change="handleFilesChange" />
        </div>
        <Button variant="outline" @click="exportUsers">
          <DownloadIcon />
          Export .xlsx
        </Button>
      </div>
    </div>

    <Table
      :data="records"
      :columns="columns"
      v-model:sort-by="formFilters.sort_by"
      v-model:order="formFilters.order"
    >
      <template #dni-cell="{ row }">
        <Link :href="managerEmployeePath(row.id)" class="text-primary">
          {{ row.dni }}
        </Link>
      </template>

      <template #start_date-cell="{ row }">
        {{ dateFormatter(row.start_date, {}, "date") }}
      </template>

      <template #completeness-cell="{ row }">
        <span v-if="row.completeness" class="font-semibold text-green-700">
          Completo
        </span>
        <span v-else class="font-semibold text-red-700">Incompleto</span>
      </template>

      <template #termination_date-cell="{ row }">
        {{
          row.termination_date
            ? dateFormatter(row.termination_date, {}, "date")
            : "—"
        }}
      </template>

      <template #updated_at-cell="{ row }">
        {{ dateFormatter(row.updated_at, {}, "dateTime") }}
      </template>

      <template #empty>
        <p class="text-muted-foreground py-6 text-center">
          No se encontraron empleados
        </p>
      </template>
    </Table>

    <Pagination class="my-4 px-6" />
  </AppLayout>
</template>
